<template>
  <ui-select-container
    class="ui-select"
    :is-empty="!hasOptions && !customDropdown"
    :active-options="activeValues"
    :loading="loading"
    :value-loading="valueLoading"
    :options-loading="optionsLoading"
    :strict-comparison="strictComparison"
    ref="selectContainer"
    v-model="innerValue"
    v-bind="selectContainerProps"
    v-on="selectContainerListeners"
  >
    <template #after>
      <slot name="after" :active-value="activeValue"/>
    </template>
    <template #before-value>
      <slot name="before" :active-value="activeValue"/>
    </template>
    <template #description>
      <slot />
    </template>
    <template #beforeOption>
      <slot name="beforeOption" :size="size" :activeValues="activeValues"/>
    </template>
    <slot name="option" :list="filterOption" :change="change" :isActiveValue="isActiveValue" :size="size">
      <ui-select-option-item
        v-for="item in filterOption"
        :key="item[idFieldName]"
        class="ui-select__item"
        :class="item.selectOptionClass"
        :value="item[titleFieldName]"
        :subtext="item[additionalOptionName]"
        :active="isActiveValue(item[idFieldName])"
        :disabled="item.isDisabledSelectOption"
        :size="size"
        @click="change(item)"
      >
        <slot
          name="optionItem"
          :item="item"
          :size="size"
        />
      </ui-select-option-item>
    </slot>
    <template #afterOption>
      <slot name="afterOption"/>
    </template>
    <template #value>
      <slot name="value" :activeValues="activeValues"/>
    </template>
    <template #selectedOption="scope">
      <slot name="selectedOption" v-bind="scope" />
    </template>
  </ui-select-container>
</template>

<script>
import UiSelectOptionItem from './UiSelectOptionItem.vue'
import { isSubstring } from '../../libs/helpTools'
import inputFocusMixin from '../../mixins/inputFocusMixin'
import selectContainerMixin from '../../mixins/selectContainerMixin'
import UiSelectContainer from './UiSelectContainer.vue'

export default {
  name: 'UiSelect',
  mixins: [inputFocusMixin, selectContainerMixin],
  components: {
    UiSelectOptionItem,
    UiSelectContainer
  },
  props: {
    value: {
      type: [Number, Array, String, Boolean, Symbol],
      default: null
    },
    list: {
      type: Array,
      default: () => []
    },
    cachedList: [Array, Object],
    additionalOptionName: {
      type: String,
      default: ''
    },
    badgeTextColor: {
      type: String,
      default: ''
    },
    badgeBackgroundColor: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    },
    valueLoading: {
      type: Boolean,
      default: false
    },
    optionsLoading: {
      type: Boolean,
      default: false
    },
    customDropdown: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    innerValue: {
      get () {
        if (this.multiple) {
          if (!this.value) return []
          return Array.isArray(this.value) ? this.value : [this.value]
        }
        return this.value
      },
      set (value) {
        this.$emit('input', value)

        if (this.multiple) this.fullValueInput()
        else this.$nextTick(() => this.valueInput(this.activeValue))

        this.blur()
      }
    },
    getItemFromList () {
      return this.strictComparison
        ? (id) => this.fullMapList.get(id)
        : (id) => this.fullMapList.get(String(id))
    },
    filterOption () {
      if (this.customFilter) return this.fullList
      try {
        return this.fullList.filter(item => isSubstring(item[this.titleFieldName], this.innerSearchValue))
      } catch (e) {
        console.error(this.title, this.$parent?.$options?._componentTag, this.fullList[0])
        return []
      }
    },
    hasOptions () {
      return !!this.filterOption?.length
    },
    fullMapList () {
      const mapList = this.getMapList(this.list)

      if (this.cachedList) {
        const cachedListArray = Array.isArray(this.cachedList) ? this.cachedList : [this.cachedList]
        const addItemToList = this.strictComparison
          ? (mapList, item) => mapList.set(item[this.idFieldName], item)
          : (mapList, item) => mapList.set(String(item[this.idFieldName]), item)
        cachedListArray.reduce(addItemToList, mapList)
      }
      return mapList
    },
    fullList () {
      return [...this.fullMapList.values()]
    },
    activeMultipleValues () {
      if (!this.innerValue?.length) return []
      return this.innerValue.reduce((values, id) => {
        const valueItem = this.getItemFromList(id)
        if (valueItem) values.push(valueItem)
        return values
      }, [])
    },
    activeValue () {
      return this.getItemFromList(this.value)
    },
    activeValues () {
      if (this.multiple) return this.activeMultipleValues

      return this.activeValue ? [this.activeValue] : []
    },
    size () {
      return this.small ? 's' : 'm'
    }
  },
  methods: {
    focus () {
      this.$refs.selectContainer?.focus()
    },
    blur () {
      this.$refs.selectContainer?.blur()
    },
    isActiveValue (id) {
      return this.multiple
        ? this.innerValue.some(value => this.isEqual(value, id))
        : this.isEqual(this.value, id)
    },
    valueInput (value) {
      this.$emit('value-input', value)
    },
    fullValueInput () {
      this.$nextTick(() => this.valueInput(this.activeMultipleValues))
    },
    scrollEndHandler () {
      this.$emit('scroll-end')
    }
  }
}
</script>

<style lang="scss" scoped>
.ui-select {
  &__item {
    cursor: pointer;
  }
}
</style>
